import { useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { useCriarInsumo, useEditarInsumo, type NovoInsumoPayload } from '../api';
import type { Insumo } from '../schemas';

// Tela 02 · Novo insumo — modal de cadastro no catálogo (desktop), fiel ao
// Figma: dados do produto, composição por princípio ativo (com pH/Kow) e
// segurança/limites de bula POR CULTURA selecionada.
const CATEGORIAS = [
  ['fungicida', 'Fungicida'],
  ['herbicida', 'Herbicida'],
  ['inseticida', 'Inseticida'],
  ['adubacao', 'Fertilizante'],
  ['nutricao', 'Nutrição/Fisiologia'],
  ['biologico', 'Biológico'],
  ['especialidade', 'Especialidade'],
  ['semente', 'Semente'],
  ['adjuvante', 'Adjuvante'],
  ['oleo_vegetal', 'Óleo vegetal'],
  ['outro', 'Outro'],
] as const;
const FORMULACOES = [
  ['SC', 'SC — suspensão concentrada'],
  ['EC', 'EC — concentrado emulsionável'],
  ['WG', 'WG — grânulos dispersíveis'],
  ['SL', 'SL — concentrado solúvel'],
  ['WP', 'WP — pó molhável'],
  ['OD', 'OD — dispersão em óleo'],
  ['', 'Outra / não se aplica'],
] as const;
const CULTURAS_BASE = ['soja', 'milho', 'cafe'];

interface AtivoForm {
  nome: string;
  concentracao: string;
  unidade: string;
  grupo: string;
  ph: string;
  kow: string;
}
interface BulaForm {
  dose: string;
  carencia: string;
  maxa: string;
  intervalo: string;
}
const ativoVazio = (): AtivoForm => ({ nome: '', concentracao: '', unidade: 'g/L', grupo: '', ph: '', kow: '' });
const bulaVazia = (): BulaForm => ({ dose: '', carencia: '', maxa: '', intervalo: '' });

function numOuNull(s: string): number | null {
  const n = Number(s.replace(',', '.'));
  return s.trim() === '' || Number.isNaN(n) ? null : n;
}
// Número → string pt-BR pro input (edição): 0.4 → "0,4".
function numStr(n: number | null): string {
  return n == null ? '' : String(n).replace('.', ',');
}
// "0,5" | "0,4 – 0,5" | "0.4-0.5" → [min, max]
function parseDose(s: string): [number | null, number | null] {
  const partes = s.split(/[–-]/).map((p) => numOuNull(p));
  if (partes.length >= 2) return [partes[0] ?? null, partes[1] ?? null];
  return [partes[0] ?? null, partes[0] ?? null];
}
function capitaliza(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
// Dose numérica (min/max) → string editável: "0,5" ou "0,4 – 0,5".
function doseParaInput(min: number | null, max: number | null): string {
  if (min == null && max == null) return '';
  if (min != null && max != null && min !== max) return `${numStr(min)} – ${numStr(max)}`;
  return numStr(max ?? min);
}

const IN =
  'w-full rounded-lg border border-black/[0.12] bg-[#fafaf9] px-[11px] py-2 text-[12.5px] text-text placeholder:text-text-muted focus:outline-none focus:border-[var(--accent)] dark:border-white/[0.12] dark:bg-white/5';
const LBL = 'text-[11.5px] font-medium text-text-2';

export function NovoInsumoModal({ onClose, insumo }: { onClose: () => void; insumo?: Insumo }) {
  const editando = insumo != null;
  const criar = useCriarInsumo();
  const editar = useEditarInsumo(insumo?.id ?? 0);
  const mutacao = editando ? editar : criar;

  const [nome, setNome] = useState(() => insumo?.nome_comercial ?? '');
  const [categoria, setCategoria] = useState(() => insumo?.categoria ?? 'fungicida');
  const [fabricante, setFabricante] = useState(() => insumo?.fabricante ?? '');
  const [formulacao, setFormulacao] = useState(() => insumo?.formulacao ?? 'SC');
  const [unidade, setUnidade] = useState(() => insumo?.unidade ?? 'L');
  const [modoAcao, setModoAcao] = useState(() => insumo?.modo_acao ?? '');
  const [numeroRegistro, setNumeroRegistro] = useState(() => insumo?.numero_registro ?? '');
  const [culturas, setCulturas] = useState<string[]>(() =>
    insumo?.culturas.length ? insumo.culturas : ['soja', 'milho'],
  );
  const [culturasExtra, setCulturasExtra] = useState<string[]>(() =>
    (insumo?.culturas ?? []).filter((c) => !CULTURAS_BASE.includes(c)),
  );
  const [novaCultura, setNovaCultura] = useState<string | null>(null);
  const [ativos, setAtivos] = useState<AtivoForm[]>(() =>
    insumo?.ativos.length
      ? insumo.ativos.map((a) => ({
          nome: a.nome,
          concentracao: numStr(a.concentracao),
          unidade: a.concentracao_unidade ?? 'g/L',
          grupo: a.grupo_quimico ?? '',
          ph: numStr(a.ph),
          kow: numStr(a.kow),
        }))
      : [ativoVazio()],
  );
  const [bula, setBula] = useState<Record<string, BulaForm>>(() => {
    const m: Record<string, BulaForm> = {};
    for (const c of insumo?.culturas_bula ?? []) {
      m[c.cultura] = {
        dose: doseParaInput(c.dose_min, c.dose_max),
        carencia: numStr(c.carencia_dias),
        maxa: numStr(c.max_aplicacoes),
        intervalo: numStr(c.intervalo_dias),
      };
    }
    return m;
  });
  const [reentrada, setReentrada] = useState(() => numStr(insumo?.reentrada_horas ?? null));
  const [classeTox, setClasseTox] = useState(() => insumo?.classe_tox ?? '');
  const [classeAmb, setClasseAmb] = useState(() => insumo?.classe_ambiental ?? '');
  const [fonteBula, setFonteBula] = useState(() => insumo?.fonte_bula_url ?? '');
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const doseUnidade = unidade === 'kg' ? 'kg/ha' : 'L/ha';
  const todasCulturas = [...CULTURAS_BASE, ...culturasExtra];

  function toggleCultura(c: string) {
    setCulturas((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  }
  function setB(c: string, patch: Partial<BulaForm>) {
    setBula((prev) => ({ ...prev, [c]: { ...(prev[c] ?? bulaVazia()), ...patch } }));
  }
  function setA(idx: number, patch: Partial<AtivoForm>) {
    setAtivos((prev) => prev.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  }

  async function salvar() {
    if (!nome.trim()) {
      setErro('Informe o nome comercial.');
      return;
    }
    setErro(null);
    const payload: NovoInsumoPayload = {
      nome_comercial: nome.trim(),
      categoria,
      fabricante: fabricante.trim() || null,
      formulacao: formulacao || null,
      unidade,
      modo_acao: modoAcao.trim() || null,
      numero_registro: numeroRegistro.trim() || null,
      culturas,
      reentrada_horas: numOuNull(reentrada),
      classe_tox: classeTox.trim() || null,
      classe_ambiental: classeAmb.trim() || null,
      fonte_bula_url: fonteBula.trim() || null,
      ativos: ativos
        .filter((a) => a.nome.trim())
        .map((a) => ({
          nome: a.nome.trim(),
          concentracao: numOuNull(a.concentracao),
          concentracao_unidade: a.unidade || null,
          grupo_quimico: a.grupo.trim() || null,
          ph: numOuNull(a.ph),
          kow: numOuNull(a.kow),
        })),
      culturas_bula: culturas.map((c) => {
        const b = bula[c] ?? bulaVazia();
        const [dmin, dmax] = parseDose(b.dose);
        return {
          cultura: c,
          dose_min: dmin,
          dose_max: dmax,
          dose_unidade: b.dose.trim() ? doseUnidade : null,
          carencia_dias: numOuNull(b.carencia),
          max_aplicacoes: numOuNull(b.maxa),
          intervalo_dias: numOuNull(b.intervalo),
        };
      }),
    };
    try {
      await mutacao.mutateAsync(payload);
      onClose();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao salvar o produto.');
    }
  }

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/45 p-4" onClick={onClose}>
      <div
        className="flex max-h-[92vh] w-full max-w-[760px] flex-col overflow-hidden rounded-[14px] bg-surface shadow-[0_12px_40px_rgba(0,0,0,0.25)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex shrink-0 items-center justify-between px-6 pb-3.5 pt-[18px]">
          <div className="flex flex-col gap-[3px]">
            <h2 className="text-[17px] font-semibold text-text">{editando ? 'Editar insumo' : 'Novo insumo'}</h2>
            <span className="text-[12.5px] text-text-muted">
              {editando
                ? 'Atualize os dados do produto — as alterações valem para as próximas recomendações.'
                : 'Cadastro no catálogo — os campos mudam conforme a categoria.'}
            </span>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar" className="text-text-muted hover:text-text">
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {/* dados do produto */}
          <div className="flex flex-col gap-3 px-6 pb-[18px] pt-2.5">
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.6px] text-accent">Dados do produto</span>
            <div className="grid grid-cols-[1fr_170px_174px] gap-3">
              <label className="flex flex-col gap-[5px]">
                <span className={LBL}>Nome comercial</span>
                <input className={IN} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Ex.: Fox Xpro" />
              </label>
              <label className="flex flex-col gap-[5px]">
                <span className={LBL}>Categoria</span>
                <select className={IN} value={categoria} onChange={(e) => setCategoria(e.target.value)}>
                  {CATEGORIAS.map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-[5px]">
                <span className={LBL}>Fabricante</span>
                <input className={IN} value={fabricante} onChange={(e) => setFabricante(e.target.value)} placeholder="Ex.: Bayer" />
              </label>
            </div>
            <div className="grid grid-cols-[260px_90px_1fr] gap-3">
              <label className="flex flex-col gap-[5px]">
                <span className={LBL}>Formulação</span>
                <select className={IN} value={formulacao} onChange={(e) => setFormulacao(e.target.value)}>
                  {FORMULACOES.map(([v, l]) => (
                    <option key={v} value={v}>{l}</option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-[5px]">
                <span className={LBL}>Unidade</span>
                <select className={IN} value={unidade} onChange={(e) => setUnidade(e.target.value)}>
                  <option value="L">L</option>
                  <option value="kg">kg</option>
                </select>
              </label>
              <label className="flex flex-col gap-[5px]">
                <span className={LBL}>Modo de ação</span>
                <input className={IN} value={modoAcao} onChange={(e) => setModoAcao(e.target.value)} placeholder="Ex.: Sistêmico + mesostêmico" />
              </label>
            </div>
            <div className="grid grid-cols-[344px_1fr] gap-3">
              <label className="flex flex-col gap-[5px]">
                <span className={LBL}>Nº de registro</span>
                <input className={IN} value={numeroRegistro} onChange={(e) => setNumeroRegistro(e.target.value)} placeholder="Ex.: Registro MAPA nº 002894" />
              </label>
              <span />
            </div>
            <div className="flex flex-col gap-[5px]">
              <span className={LBL}>Culturas</span>
              <div className="flex flex-wrap items-center gap-1.5">
                {todasCulturas.map((c) => {
                  const on = culturas.includes(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => toggleCultura(c)}
                      className={`rounded-full px-2.5 py-1 text-xs ${
                        on
                          ? 'bg-[rgba(233,106,69,0.13)] font-medium text-[#c94f2b] dark:text-[#f59e78]'
                          : 'border border-black/[0.15] text-text-muted dark:border-white/[0.15]'
                      }`}
                    >
                      {capitaliza(c)}
                    </button>
                  );
                })}
                {novaCultura === null ? (
                  <button
                    type="button"
                    onClick={() => setNovaCultura('')}
                    className="rounded-full border border-black/[0.15] px-2.5 py-1 text-xs text-text-muted dark:border-white/[0.15]"
                  >
                    + cultura
                  </button>
                ) : (
                  <input
                    autoFocus
                    value={novaCultura}
                    onChange={(e) => setNovaCultura(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && novaCultura.trim()) {
                        const c = novaCultura.trim().toLowerCase();
                        if (!todasCulturas.includes(c)) setCulturasExtra((p) => [...p, c]);
                        setCulturas((p) => (p.includes(c) ? p : [...p, c]));
                        setNovaCultura(null);
                      }
                      if (e.key === 'Escape') setNovaCultura(null);
                    }}
                    onBlur={() => setNovaCultura(null)}
                    placeholder="cultura + Enter"
                    className="w-32 rounded-full border border-black/[0.15] px-2.5 py-1 text-xs text-text focus:outline-none dark:border-white/[0.15]"
                  />
                )}
              </div>
            </div>
          </div>

          {/* composição */}
          <div className="flex flex-col items-start gap-2 border-t border-black/[0.08] px-6 pb-4 pt-3.5 dark:border-white/[0.08]">
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.6px] text-accent">
              Composição — por princípio ativo
            </span>
            <div className="w-full overflow-hidden rounded-[9px] border border-black/10 bg-[#fafaf9] dark:border-white/10 dark:bg-white/[0.03]">
              <div className="grid grid-cols-[1fr_120px_150px_60px_70px_28px] gap-3 bg-[#f6f6f5] px-3 py-2 dark:bg-white/[0.04]">
                {['Princípio ativo', 'Concentração', 'Grupo químico', 'pH', 'Kow (log P)', ''].map((h, i) => (
                  <span key={i} className="text-[11px] font-medium text-text-muted">{h}</span>
                ))}
              </div>
              {ativos.map((a, idx) => (
                <div key={idx} className="grid grid-cols-[1fr_120px_150px_60px_70px_28px] items-center gap-3 border-t border-black/[0.08] px-3 py-2 dark:border-white/[0.08]">
                  <input className={IN} value={a.nome} onChange={(e) => setA(idx, { nome: e.target.value })} placeholder="Ex.: bixafem" />
                  <div className="flex items-center gap-1">
                    <input className={IN} value={a.concentracao} onChange={(e) => setA(idx, { concentracao: e.target.value })} placeholder="125" />
                    <select
                      value={a.unidade}
                      onChange={(e) => setA(idx, { unidade: e.target.value })}
                      className="rounded-lg border border-black/[0.12] bg-[#fafaf9] px-1 py-2 text-[11px] text-text-2 focus:outline-none dark:border-white/[0.12] dark:bg-white/5"
                    >
                      <option>g/L</option>
                      <option>g/kg</option>
                      <option>%</option>
                    </select>
                  </div>
                  <input className={IN} value={a.grupo} onChange={(e) => setA(idx, { grupo: e.target.value })} placeholder="Carboxamida" />
                  <input className={IN} value={a.ph} onChange={(e) => setA(idx, { ph: e.target.value })} placeholder="6,3" />
                  <input className={IN} value={a.kow} onChange={(e) => setA(idx, { kow: e.target.value })} placeholder="3,3" />
                  <button
                    type="button"
                    aria-label="Remover ativo"
                    onClick={() => setAtivos((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev))}
                    className="text-text-muted hover:text-[var(--danger)]"
                  >
                    <X size={13} strokeWidth={2} />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setAtivos((prev) => [...prev, ativoVazio()])}
              className="flex items-center gap-1.5 rounded-[7px] px-2 py-[5px] text-xs font-medium text-accent hover:bg-[var(--accent-100)]"
            >
              <Plus size={12} strokeWidth={2.25} /> Adicionar ativo
            </button>
          </div>

          {/* segurança e limites de bula */}
          <div className="flex flex-col gap-3 border-t border-black/[0.08] px-6 pb-5 pt-3.5 dark:border-white/[0.08]">
            <div className="flex items-center justify-between">
              <span className="text-[10.5px] font-semibold uppercase tracking-[0.6px] text-accent">
                Segurança e limites de bula
              </span>
              <span className="text-[11px] text-text-muted">um bloco por cultura selecionada acima</span>
            </div>
            {culturas.map((c) => {
              const b = bula[c] ?? bulaVazia();
              return (
                <div key={c} className="flex items-end gap-3">
                  <span className="mb-1.5 shrink-0 rounded-full bg-[rgba(233,106,69,0.13)] px-3 py-[5px] text-xs font-medium text-[#c94f2b] dark:text-[#f59e78]">
                    {capitaliza(c)}
                  </span>
                  <label className="flex min-w-0 flex-1 flex-col gap-[5px]">
                    <span className={LBL}>Dose (bula)</span>
                    <div className="relative">
                      <input className={`${IN} pr-14`} value={b.dose} onChange={(e) => setB(c, { dose: e.target.value })} placeholder="0,4 – 0,5" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-text-muted">{doseUnidade}</span>
                    </div>
                  </label>
                  <label className="flex w-[110px] flex-col gap-[5px]">
                    <span className={LBL}>Carência</span>
                    <div className="relative">
                      <input className={`${IN} pr-10`} value={b.carencia} onChange={(e) => setB(c, { carencia: e.target.value })} placeholder="30" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-text-muted">dias</span>
                    </div>
                  </label>
                  <label className="flex w-[96px] flex-col gap-[5px]">
                    <span className={LBL}>Máx. aplic.</span>
                    <input className={IN} value={b.maxa} onChange={(e) => setB(c, { maxa: e.target.value })} placeholder="2" />
                  </label>
                  <label className="flex w-[110px] flex-col gap-[5px]">
                    <span className={LBL}>Intervalo</span>
                    <div className="relative">
                      <input className={`${IN} pr-10`} value={b.intervalo} onChange={(e) => setB(c, { intervalo: e.target.value })} placeholder="14" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-text-muted">dias</span>
                    </div>
                  </label>
                </div>
              );
            })}
            <div className="flex gap-3">
              <label className="flex w-[110px] flex-col gap-[5px]">
                <span className={LBL}>Reentrada</span>
                <div className="relative">
                  <input className={`${IN} pr-7`} value={reentrada} onChange={(e) => setReentrada(e.target.value)} placeholder="24" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-text-muted">h</span>
                </div>
              </label>
              <label className="flex flex-1 flex-col gap-[5px]">
                <span className={LBL}>Classe toxicológica</span>
                <input className={IN} value={classeTox} onChange={(e) => setClasseTox(e.target.value)} placeholder="Ex.: Categoria 5 — improvável de causar dano" />
              </label>
              <label className="flex flex-1 flex-col gap-[5px]">
                <span className={LBL}>Classe ambiental</span>
                <input className={IN} value={classeAmb} onChange={(e) => setClasseAmb(e.target.value)} placeholder="Ex.: III — perigoso" />
              </label>
            </div>
            <label className="flex flex-col gap-[5px]">
              <span className={LBL}>Fonte da bula (ADAPAR)</span>
              <input className={IN} value={fonteBula} onChange={(e) => setFonteBula(e.target.value)} placeholder="https://www.adapar.pr.gov.br/…" />
            </label>
            {erro && <p className="text-[12px] text-[var(--danger)]">{erro}</p>}
          </div>
        </div>

        {/* footer */}
        <div className="flex shrink-0 items-center gap-2 border-t border-black/[0.08] bg-[#fafaf9] px-6 py-3.5 dark:border-white/[0.08] dark:bg-white/[0.03]">
          <span className="flex-1 text-[11.5px] text-text-muted">
            Fonte: bula ADAPAR — confira a bula vigente antes de salvar.
          </span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-black/[0.15] bg-surface px-[18px] py-[11px] text-[13px] font-medium text-text dark:border-white/[0.15]"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void salvar()}
            disabled={mutacao.isPending}
            className="rounded-full bg-[var(--accent)] px-[18px] py-[11px] text-[13px] font-medium text-white disabled:opacity-60"
          >
            {mutacao.isPending ? 'Salvando…' : editando ? 'Salvar alterações' : 'Salvar produto'}
          </button>
        </div>
      </div>
    </div>
  );
}
